import { useEffect, useState } from "react";
import { Landmark, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "rounded-none focus:ring-2 focus:ring-blue-800";
const blank = {
  account_holder_name: "", account_number: "", ifsc: "", pan: "",
  beneficiary_email: "", beneficiary_phone: "", city: "", state: "", pincode: "",
};

const statusClass = (s) =>
  s === "activated" ? "bg-green-50 text-green-900"
  : s === "needs_clarification" || s === "suspended" ? "bg-red-50 text-red-900"
  : "bg-amber-50 text-amber-900";

/** Registers the arbitrator's own bank account with Razorpay Route. AdhocArb never holds the fee. */
export const BankPayouts = () => {
  const [state, setState] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => {
    api.get("/payouts/bank-account").then(({ data }) => setState(data)).catch(() => {});
    api.get("/dashboard/payouts").then(({ data }) => setPayouts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const { data } = await api.post("/payouts/bank-account", form);
      setState(data);
      setForm(blank);
      setEditing(false);
      toast.success("Bank account registered for direct payouts");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.get("/payouts/bank-account/status");
      setState(data);
      toast.success("Status refreshed");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  if (!state) return <div className="h-40 border border-border bg-muted animate-pulse" />;
  const bank = state.bank;

  return (
    <section className="border border-border bg-card p-7" data-testid="bank-payouts">
      <p className="flex items-center gap-2 label-caps">
        <Landmark className="h-3.5 w-3.5" /> Bank account & payouts
      </p>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
        Your fees are transferred by Razorpay straight from the paying party to your own bank
        account. <strong>AdhocArb neither holds nor controls your fee</strong> at any stage. Where a
        tribunal sits in more than one member, a fee note is divided equally between the members'
        accounts.
      </p>

      {!state.route_enabled && (
        <p className="mt-5 text-sm text-amber-800 dark:text-amber-400 border border-amber-700/40 bg-amber-50 dark:bg-amber-950/30 p-4"
          data-testid="route-not-configured">
          Direct payouts are not yet switched on for this registry. You may still record your
          details once Razorpay Route is activated on the AdhocArb account.
        </p>
      )}

      {bank && !editing ? (
        <>
          <dl className="mt-6 space-y-2.5 text-sm" data-testid="bank-summary">
            {[
              ["Account holder", bank.account_holder_name, "holder"],
              ["Account number", bank.account_number, "number"],
              ["IFSC", bank.ifsc, "ifsc"],
              ["PAN", bank.pan, "pan"],
              ["Beneficiary email", bank.beneficiary_email, "email"],
              ["Beneficiary mobile", bank.beneficiary_phone, "phone"],
            ].filter(([, v]) => v).map(([k, v, id]) => (
              <div key={id} className="flex justify-between gap-6">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-mono text-xs" data-testid={`bank-${id}`}>{v}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-6 pt-1">
              <dt className="text-muted-foreground">Payout status</dt>
              <dd>
                <Badge className={statusClass(bank.activation_status)} testId="bank-status">
                  {(bank.activation_status || "created").replace(/_/g, " ")}
                </Badge>
              </dd>
            </div>
            {bank.added_on && (
              <div className="flex justify-between gap-6">
                <dt className="text-muted-foreground">Registered on</dt>
                <dd>{fmtDate(bank.added_on)}</dd>
              </div>
            )}
          </dl>
          <p className="flex items-start gap-2 text-[11px] text-muted-foreground mt-4">
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Your particulars are masked here and are never shown to any party, counsel or to the
            registry.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={() => setEditing(true)} data-testid="bank-replace-button"
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white uppercase tracking-widest text-xs h-10 px-4">
              Replace account
            </Button>
            {state.route_enabled && (
              <Button onClick={refresh} disabled={!!busy} data-testid="bank-refresh-button"
                className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-4">
                <RefreshCw className="h-3.5 w-3.5 mr-2" />
                {busy === "refresh" ? "Checking…" : "Refresh status"}
              </Button>
            )}
          </div>
        </>
      ) : (
        <form onSubmit={save} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="bank-form">
          {[
            ["account_holder_name", "Account holder's name", "text", true],
            ["account_number", "Account number", "text", true],
            ["ifsc", "IFSC", "text", true],
            ["pan", "PAN", "text", true],
            ["beneficiary_email", "Beneficiary email", "email", true],
            ["beneficiary_phone", "Beneficiary mobile", "text", false],
            ["city", "City", "text", false],
            ["state", "State", "text", false],
            ["pincode", "PIN code", "text", false],
          ].map(([k, label, type, required]) => (
            <div key={k} className="space-y-2">
              <Label className="label-caps">{label}</Label>
              <Input data-testid={`bank-${k}-input`} type={type} required={required}
                value={form[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} />
            </div>
          ))}
          <div className="sm:col-span-2 flex flex-wrap gap-3">
            <Button type="submit" disabled={!!busy} data-testid="bank-save-button"
              className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 px-6">
              {busy === "save" ? "Registering…" : "Register account"}
            </Button>
            {bank && (
              <button type="button" onClick={() => { setEditing(false); setForm(blank); }}
                data-testid="bank-cancel-button"
                className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {payouts?.rows?.length > 0 && (
        <div className="mt-8">
          <p className="label-caps">Transferred to you</p>
          <p className="font-display text-2xl mt-2" data-testid="routed-total">{money(payouts.routed_total)}</p>
          <ul className="mt-4 divide-y divide-border border border-border" data-testid="payout-rows">
            {payouts.rows.map((r, i) => (
              <li key={`${r.invoice_number}-${i}`} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                data-testid={`payout-row-${i}`}>
                <span className="font-mono text-xs">{r.invoice_number}</span>
                <span className="font-mono text-sm">{money(r.amount)}</span>
                <Badge className={r.status === "processed" || r.status === "settled" ? "bg-green-50 text-green-900" : r.status === "failed" ? "bg-red-50 text-red-900" : "bg-amber-50 text-amber-900"}>
                  {r.status}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
